import { AlertCircle, CheckCircle, Loader2, Lock } from 'lucide-react';
import { useState } from 'react';
import apiClient from '../../sdk/apiClient';

const AuthScreen = ({ onAuthSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const handleAuthenticate = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get API key
      const apiKey = await apiClient.getApiKey();

      if (apiKey) {
        setIsAuthenticated(true);
        // Wait a moment to show success state
        setTimeout(() => {
          onAuthSuccess(apiKey);
        }, 1000);
      } else {
        console.error('No API key received');
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      console.error('Auth Error in handleAuthenticate:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="card">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {isAuthenticated ? (
                <CheckCircle size={40} className="text-green-500" />
              ) : (
                <Lock size={40} className="text-blue-500" />
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {isAuthenticated ? 'Authentication Successful!' : 'Authentication Required'}
            </h2>
            <p className="text-gray-600">
              {isAuthenticated 
                ? 'You can now proceed with the verification process.'
                : 'Please authenticate to access the NID verification service.'}
            </p>
          </div>

          {/* Info Note */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This will authenticate you with the NID verification service using secure credentials.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold mb-1">Authentication Error</p>
                  <p>{error}</p>
                  <p className="mt-2 text-xs text-red-600">
                    Please check the browser console for more details.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={isAuthenticated ? onAuthSuccess : handleAuthenticate}
              className="btn-primary flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" size={20} />
                  Authenticating...
                </span>
              ) : isAuthenticated ? (
                'Continue'
              ) : (
                'Authenticate'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
